import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { usePreferences } from './PreferencesContext';
import { useWorkspace } from './WorkspaceContext';
import { fetchCurrencies } from '../api/endpointsBackend';
import { resolveHomeCurrency } from '../utils/homeCurrency';
import { currencyForCountry } from '../utils/onboardingCountries';

// MI-298: app-level "display currency". When set, totals surfaces show a single
// approximate total converted into this currency; when null, the default
// per-currency view is shown. Deliberately NOT persisted across sessions: the
// conversion is an on-demand view, so nothing is carried over from the last one.
// MI-311 (revision 7): instead of always opening on "per currency", each load
// starts on the user's home currency when one can be resolved — see
// utils/homeCurrency.js. Still no persistence: the default is recomputed, never
// remembered, and the user's own pick owns the rest of the session.
const LEGACY_CACHE_KEY = 'mi-kapital-display-currency';

const DisplayCurrencyContext = createContext(null);

export function DisplayCurrencyProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { currentId } = useWorkspace();
  const { country, homeCurrency, setHomeCurrency } = usePreferences();
  const [displayCurrency, setDisplayCurrencyState] = useState(null);
  const [activeCurrencies, setActiveCurrencies] = useState([]);
  // Flips on the first explicit pick (or once the default lands), so the default
  // can never overwrite a choice the user made.
  const chosen = useRef(false);

  // One-time cleanup of the persisted value from earlier builds, so users who
  // picked a currency before this change don't keep landing on it forever.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (e) {
      /* ignore privacy-mode errors */
    }
  }, []);

  // The active currencies, loaded once per session: the selector lists them and
  // the home-currency default needs them to know what it is allowed to pick.
  // Currencies are workspace-scoped, so this waits for the workspace: fired
  // before it resolves, on a device's first session, the request has no
  // workspace header and fails. One retry covers a transient failure.
  useEffect(() => {
    if (!isAuthenticated || !currentId) return undefined;
    let active = true;
    let retry = null;
    const load = (attemptsLeft) => fetchCurrencies(true)
      .then((rows) => {
        if (active) setActiveCurrencies((rows || []).map((c) => c.name));
      })
      .catch(() => {
        // Then the selector hides and the default stands down — non-blocking.
        if (active && attemptsLeft > 0) retry = setTimeout(() => load(attemptsLeft - 1), 1500);
      });
    load(1);
    return () => {
      active = false;
      clearTimeout(retry);
    };
  }, [isAuthenticated, currentId]);

  // MI-337: users who onboarded before the currency was stored have a country
  // and no `home_currency`. Fill it in once, from the country only — never from
  // the time-zone or language guesses `resolveHomeCurrency` falls back to,
  // because persisting a guess would freeze the wrong currency into the profile
  // of anyone travelling. Gated on the currency being active, like every other
  // reader of this value.
  useEffect(() => {
    if (homeCurrency || !country || !activeCurrencies.length) return;
    const fromCountry = currencyForCountry(country);
    if (fromCountry && activeCurrencies.includes(fromCountry)) {
      setHomeCurrency(fromCountry);
    }
  }, [homeCurrency, country, activeCurrencies, setHomeCurrency]);

  // `null` (or the sentinel 'all') means "per-currency view".
  const setDisplayCurrency = useCallback((code) => {
    chosen.current = true;
    setDisplayCurrencyState(code && code !== 'all' ? code : null);
  }, []);

  // Open on the home currency. This re-runs as `country` arrives (the cached
  // value is there on first paint; the DB value lands on hydration) and only
  // commits once something resolves, so a not-yet-hydrated country can't lock in
  // a time-zone guess. Unresolvable = stay on the per-currency view.
  useEffect(() => {
    if (chosen.current || !activeCurrencies.length) return;
    const home = resolveHomeCurrency(country, activeCurrencies);
    if (!home) return;
    chosen.current = true;
    setDisplayCurrencyState(home);
  }, [country, activeCurrencies]);

  const value = { displayCurrency, setDisplayCurrency, activeCurrencies };
  return (
    <DisplayCurrencyContext.Provider value={value}>
      {children}
    </DisplayCurrencyContext.Provider>
  );
}

export function useDisplayCurrency() {
  const ctx = useContext(DisplayCurrencyContext);
  if (!ctx) {
    throw new Error('useDisplayCurrency must be used inside DisplayCurrencyProvider');
  }
  return ctx;
}
